import * as React from 'react'
import { Check, ChevronsUpDown, Globe, Pencil, Plus, Save, Search, Trash2, UserRound } from 'lucide-react'
import { AgentAvatar } from '@/components/agents/AgentVisualEditor'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { loadAgentVisualsMap, type StoredAgentVisual } from '@/lib/agent-visuals'
import { cn } from '@/lib/utils'
import { agentsService } from '@/services/agentsService'
import { RagApiError } from '@/services/ApiService'
import {
  conversationsApi,
  ConversationsApiError,
  conversationsErrorToUserMessage,
  type ChannelCatalogEntry,
  type ConversationSource,
} from '@/services/conversationsApi'
import { log } from '@/lib/logger'
import { toast } from 'sonner'
import { useNavigate, useParams } from 'react-router-dom'
import type { Agent, AgentConnection, AgentConnectionStatus } from '@/types/agent'
import { Switch } from '@/components/ui/switch'
import { whatsappService, type WhatsAppNumber } from '@/services/whatsappService'
import {
  connectionBelongsToRow,
  connectionNumber,
  rowKey,
} from '@/services/channelRowScope'

const SOURCE_ICON_SRC: Record<string, string> = {
  whatsapp: 'https://cdn.simpleicons.org/whatsapp/25D366',
  instagram: 'https://cdn.simpleicons.org/instagram/E4405F',
  facebook: 'https://cdn.simpleicons.org/messenger/00B2FF',
  messenger: 'https://cdn.simpleicons.org/messenger/00B2FF',
  wordpress: 'https://cdn.simpleicons.org/wordpress/21759B',
}

type ChannelDraft = {
  agent_id: string
  is_default: boolean
  /** Estado on/off de la conexión. Se arrastra al reasignar para no reactivar sin querer. */
  status?: AgentConnectionStatus
  /** Id de la conexión existente, necesario para el PATCH del interruptor. */
  connection_id?: string
  /**
   * `source_key` real de la conexión, que puede diferir de la clave del canal en el
   * catálogo (el avatar es `liveavatar` en la conexión y `Avatar` en el catálogo).
   */
  source_key?: string
  /** El número que reclama esta conexión, si reclama alguno. */
  phone_number_id?: string
}

type ChannelRow = {
  source_key: string
  display_name: string
  channel_id?: string
  is_custom: boolean
  /**
   * El número concreto al que pertenece la fila, cuando el canal tiene varios.
   * Vacío significa «el resto del canal»: el agente que contesta por cualquier
   * número que no tenga el suyo.
   */
  phone_number_id?: string
  /** El teléfono tal y como lo lee una persona, para no enseñar el id de Meta. */
  phone_label?: string
}



function normalizeId(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return 'none'
  return String(value)
}

/**
 * Alias del canal del avatar. El catálogo lo publica como `Avatar`, la conexión del portal
 * usa `liveavatar` y el backend mapea además `heygen`. Se unifican bajo una sola clave para
 * que la fila del catálogo encuentre su conexión: si no, saldría siempre "sin agente".
 */
const AVATAR_SOURCE_ALIASES = new Set(['avatar', 'liveavatar', 'heygen'])
const AVATAR_CANONICAL_KEY = 'avatar'

function normalizeSourceKey(value: string): string {
  const normalized = value.trim().toLowerCase()
  return AVATAR_SOURCE_ALIASES.has(normalized) ? AVATAR_CANONICAL_KEY : normalized
}

function sourceVisualKey(raw: string): string {
  const normalized = normalizeSourceKey(raw)
  if (normalized === 'word press') return 'wordpress'
  return normalized
}

// El avatar del portal de compradores (comprar.rkiglesias.com). Al pasar su conexión a un
// estado distinto de `connected`, el portal deja de mostrarlo: lo consulta al arrancar y lo
// cachea, así que el cambio se propaga en unos minutos sin redesplegar la web.
//
// Van las dos claves a propósito: el catálogo de canales publica este canal como `Avatar`,
// mientras que la conexión del portal usa `source_key: 'liveavatar'`. La fila se construye
// desde el catálogo, así que sin `avatar` no aparecería ninguna.
const ALLOWED_CHANNEL_SOURCE_KEYS = new Set([
  'web',
  'whatsapp',
  'instagram',
  'facebook',
  'messenger',
  'avatar',
  'liveavatar',
])

function toErrorMessage(error: unknown): string {
  if (error instanceof ConversationsApiError) {
    return conversationsErrorToUserMessage(error)
  }
  if (error instanceof RagApiError && error.body && typeof error.body === 'object') {
    const b = error.body as { error?: { message?: string } }
    if (b.error?.message) return b.error.message
  }
  if (error && typeof error === 'object' && 'body' in error) {
    const body = (error as { body?: unknown }).body
    if (body && typeof body === 'object') {
      const detail = (body as Record<string, unknown>).error || (body as Record<string, unknown>).detail
      if (detail) return String(detail)
    }
  }
  if (error instanceof Error) return error.message
  return 'Ha ocurrido un error inesperado.'
}

function SourceIcon({ sourceKey }: { sourceKey: string }) {
  const key = sourceVisualKey(sourceKey)
  const iconSrc = SOURCE_ICON_SRC[key]
  if (iconSrc) {
    return <img key={iconSrc} src={iconSrc} alt="" aria-hidden="true" className="h-4 w-4 opacity-90" onError={(e) => { e.currentTarget.style.display = 'none' }} />
  }
  if (key === 'web') {
    return <Globe className="h-4 w-4 text-primary" aria-hidden="true" />
  }
  if (key === 'liveavatar' || key === 'avatar' || key === 'heygen') {
    return <UserRound className="h-4 w-4 text-primary" aria-hidden="true" />
  }
  return <span className="inline-block h-2.5 w-2.5 rounded-full bg-muted/70" aria-hidden="true" />
}

function AgentSummary({
  agent,
  visual,
}: {
  agent: Agent
  visual?: StoredAgentVisual
}) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
      <div className="h-8 w-8 shrink-0 overflow-hidden rounded-md bg-muted/40">
        <AgentAvatar visual={visual} fallbackClassName="bg-muted/30" />
      </div>
      <div className="min-w-0 flex-1 overflow-hidden">
        <p className="truncate text-sm font-medium" title={agent.display_name}>
          {agent.display_name}
        </p>
        <p
          className="line-clamp-1 break-all text-xs text-muted-foreground"
          title={agent.description?.trim() || agent.name}
        >
          {agent.description?.trim() || agent.name}
        </p>
      </div>
    </div>
  )
}

export function ChannelAgentsPage() {
  const navigate = useNavigate()
  const { lang } = useParams<{ lang: string }>()
  const langPrefix = `/${lang || 'en'}`

  const [enabledAgents, setEnabledAgents] = React.useState<Agent[]>([])
  const [agentVisuals, setAgentVisuals] = React.useState<Record<string, StoredAgentVisual>>({})

  const [customChannels, setCustomChannels] = React.useState<ConversationSource[]>([])
  const [allConversationSources, setAllConversationSources] = React.useState<ConversationSource[]>([])
  const [eligibleCatalogChannels, setEligibleCatalogChannels] = React.useState<ChannelCatalogEntry[]>([])

  const [whatsappNumbers, setWhatsappNumbers] = React.useState<WhatsAppNumber[]>([])
  const [connectionIndexBySource, setConnectionIndexBySource] = React.useState<Record<string, ChannelDraft>>({})
  const [draftsBySource, setDraftsBySource] = React.useState<Record<string, string>>({})

  const [loading, setLoading] = React.useState(true)
  const [savingSourceKey, setSavingSourceKey] = React.useState<string | null>(null)

  const [deletingSourceId, setDeletingSourceId] = React.useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [channelPendingDelete, setChannelPendingDelete] = React.useState<ConversationSource | null>(null)

  const [createOpen, setCreateOpen] = React.useState(false)
  const [newChannelKey, setNewChannelKey] = React.useState('')
  const [newChannelName, setNewChannelName] = React.useState('')
  const [createAssignedAgentId, setCreateAssignedAgentId] = React.useState('none')
  const [creatingChannel, setCreatingChannel] = React.useState(false)

  const [editOpen, setEditOpen] = React.useState(false)
  const [editingRow, setEditingRow] = React.useState<ChannelRow | null>(null)
  const [editChannelName, setEditChannelName] = React.useState('')
  const [updatingChannel, setUpdatingChannel] = React.useState(false)

  const [openSourceSelector, setOpenSourceSelector] = React.useState<string | null>(null)
  const [sourceSearchTerm, setSourceSearchTerm] = React.useState('')

  const ragAgents = React.useMemo(
    () => enabledAgents.filter((agent) => agent.name !== 'master-graph'),
    [enabledAgents],
  )

  const createSourceAlreadyExists = React.useMemo(() => {
    const normalized = normalizeSourceKey(newChannelKey)
    if (!normalized) return false
    return allConversationSources.some((row) => normalizeSourceKey(row.key) === normalized)
  }, [newChannelKey, allConversationSources])

  const channelRows = React.useMemo<ChannelRow[]>(() => {
    const byKey = new Map<string, ChannelRow>()
    for (const row of eligibleCatalogChannels) {
      if (!ALLOWED_CHANNEL_SOURCE_KEYS.has(normalizeSourceKey(row.key))) continue
      byKey.set(normalizeSourceKey(row.key), {
        source_key: row.key,
        display_name: row.display_name || row.key,
        is_custom: false,
      })
    }
    for (const custom of customChannels) {
      if (!ALLOWED_CHANNEL_SOURCE_KEYS.has(normalizeSourceKey(custom.key))) continue
      byKey.set(normalizeSourceKey(custom.key), {
        source_key: custom.key,
        display_name: custom.display_name || custom.key,
        channel_id: custom.id,
        is_custom: true,
      })
    }

    // El avatar no llega por el catálogo: `conversations` lo excluye a propósito para que
    // no aparezca entre los filtros del inbox (hay tests que lo fijan). Pero su conexión
    // sí existe, y es la que gobierna el interruptor, así que la fila se deriva de ella.
    const avatarConnection = connectionIndexBySource[AVATAR_CANONICAL_KEY]
    if (avatarConnection && !byKey.has(AVATAR_CANONICAL_KEY)) {
      byKey.set(AVATAR_CANONICAL_KEY, {
        source_key: avatarConnection.source_key || AVATAR_CANONICAL_KEY,
        display_name: 'Avatar (portal web)',
        is_custom: false,
      })
    }

    const filas = Array.from(byKey.values()).sort((a, b) =>
      a.source_key.localeCompare(b.source_key),
    )

    // Cada número conectado es su propia fila, pero **solo cuando hay más de uno**:
    // con un único número la fila del canal ya es ese número, y desdoblarla
    // enseñaría dos veces lo mismo. Con dos, cada uno lo atiende quien deba y la
    // fila del canal se queda para los que no tengan agente propio.
    const whatsappRow = filas.find((f) => normalizeSourceKey(f.source_key) === 'whatsapp')
    if (!whatsappRow || whatsappNumbers.length < 2) return filas

    const porNumero: ChannelRow[] = whatsappNumbers.map((numero) => ({
      source_key: whatsappRow.source_key,
      display_name: numero.display_name || numero.display_phone_number || 'WhatsApp',
      is_custom: false,
      phone_number_id: numero.phone_number_id,
      phone_label: numero.display_phone_number || numero.phone_number_id,
    }))

    const salida: ChannelRow[] = []
    for (const fila of filas) {
      salida.push(fila)
      if (fila === whatsappRow) salida.push(...porNumero)
    }
    return salida
  }, [eligibleCatalogChannels, customChannels, connectionIndexBySource, whatsappNumbers])

  const changedSources = React.useMemo(() => {
    return channelRows.filter((row) => {
      const key = rowKey(row.source_key, row.phone_number_id)
      const current = connectionIndexBySource[key] || { agent_id: 'none', is_default: false }
      const selectedAgentId = draftsBySource[key] ?? current.agent_id
      return selectedAgentId !== current.agent_id
    })
  }, [channelRows, connectionIndexBySource, draftsBySource])

  const filteredAgents = React.useMemo(() => {
    const query = sourceSearchTerm.trim().toLowerCase()
    if (!query) return ragAgents
    return ragAgents.filter((agent) => {
      return (
        agent.display_name.toLowerCase().includes(query) ||
        agent.name.toLowerCase().includes(query) ||
        (agent.description || '').toLowerCase().includes(query)
      )
    })
  }, [ragAgents, sourceSearchTerm])

  const loadData = React.useCallback(async (options?: { withGlobalLoading?: boolean }) => {
    const withGlobalLoading = options?.withGlobalLoading ?? true
    try {
      if (withGlobalLoading) setLoading(true)

      const [agentsResponse, customListResult, catalogResult] = await Promise.all([
        agentsService.listAgents({ enabled: true }),
        conversationsApi.listConversationSources().catch((e) => {
          log.warn('ChannelAgentsPage listConversationSources', e instanceof ConversationsApiError ? e.message : e)
          if (withGlobalLoading) {
            toast.error(conversationsErrorToUserMessage(e))
          }
          return [] as ConversationSource[]
        }),
        conversationsApi.getChannelCatalog().catch((e) => {
          log.warn('ChannelAgentsPage getChannelCatalog', e instanceof Error ? e.message : e)
          return { channels: [] as ChannelCatalogEntry[] }
        }),
      ])

      const ragAgentsLocal = agentsResponse.filter((a) => a.name !== 'master-graph')
      const customRows = customListResult
        .filter((row) => !row.is_system)
        .sort((a, b) => a.key.localeCompare(b.key))
      const eligibleCatalog = (Array.isArray(catalogResult.channels) ? catalogResult.channels : [])
        .filter((row) => row.supports_internal_rag_agent)
        .sort((a, b) => a.key.localeCompare(b.key))

      // Los números conectados salen de `conversations`; si no se pueden leer, la
      // pantalla sigue funcionando con la fila del canal, como hasta ahora.
      try {
        setWhatsappNumbers((await whatsappService.getConnections()).filter((n) => n.phone_number_id))
      } catch (error) {
        log.warn('ChannelAgentsPage getConnections failed', { error })
        setWhatsappNumbers([])
      }

      setEnabledAgents(agentsResponse)
      setAgentVisuals(loadAgentVisualsMap())
      setAllConversationSources(customListResult)
      setCustomChannels(customRows)
      setEligibleCatalogChannels(eligibleCatalog)

      const connectionLists = await Promise.all(
        ragAgentsLocal.map(async (agent) => {
          try {
            const items = await agentsService.listAgentConnections(agent.id)
            return { agentId: normalizeId(agent.id), items }
          } catch (error) {
            log.warn('ChannelAgentsPage listAgentConnections failed while loading index', {
              agentId: agent.id,
              error,
            })
            return { agentId: normalizeId(agent.id), items: [] as AgentConnection[] }
          }
        }),
      )

      const nextIndex: Record<string, ChannelDraft> = {}
      for (const group of connectionLists) {
        for (const conn of group.items) {
          // Se incluyen también las conexiones desactivadas: si se descartaran, un canal
          // apagado desaparecería de la tabla y no habría forma de volver a encenderlo.
          // Solo se ignoran los estados que no representan un canal operable.
          if (conn.status !== 'connected' && conn.status !== 'disabled') continue
          // Una conexión que reclama un número va a la fila de ese número, no a
          // la del canal: decir que ese agente atiende todo WhatsApp no es cierto.
          const key = rowKey(conn.source_key, connectionNumber(conn) || undefined)
          const candidate: ChannelDraft = {
            agent_id: group.agentId,
            is_default: Boolean(conn.is_default),
            status: conn.status,
            connection_id: String(conn.id),
            source_key: conn.source_key,
            phone_number_id: connectionNumber(conn) || undefined,
          }
          const current = nextIndex[key]
          // Gana la conexión `connected`, y entre iguales la marcada como default.
          const candidateIsActive = candidate.status === 'connected'
          const currentIsActive = current?.status === 'connected'
          const wins =
            !current ||
            (candidateIsActive && !currentIsActive) ||
            (candidateIsActive === currentIsActive && candidate.is_default && !current.is_default)
          if (wins) {
            nextIndex[key] = candidate
          }
        }
      }

      setConnectionIndexBySource(nextIndex)

      const nextDrafts: Record<string, string> = {}
      const allRows = new Map<string, string>()
      eligibleCatalog.forEach((row) => allRows.set(normalizeSourceKey(row.key), row.key))
      customRows.forEach((row) => allRows.set(normalizeSourceKey(row.key), row.key))
      for (const key of allRows.keys()) {
        const current = nextIndex[key] || { agent_id: 'none', is_default: false }
        nextDrafts[key] = current.agent_id
      }
      setDraftsBySource(nextDrafts)

      setCreateAssignedAgentId((prev) => {
        const availableIds = ragAgentsLocal.map((a) => normalizeId(a.id))
        if (availableIds.length === 0) return 'none'
        if (prev !== 'none' && availableIds.includes(prev)) return prev
        return availableIds[0]
      })
    } catch (loadError) {
      log.error('ChannelAgentsPage loadData failed', loadError, { component: 'ChannelAgentsPage' })
      toast.error(toErrorMessage(loadError))
    } finally {
      if (withGlobalLoading) setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  const applyConnectionState = React.useCallback(
    async (sourceKey: string, next: ChannelDraft) => {
      const normalizedSource = normalizeSourceKey(sourceKey)
      if (!normalizedSource) return

      // Se retira solo lo que ocupa este mismo sitio: la conexión de un número
      // concreto, o la del canal entero. Si no se distinguiese, asignar el agente
      // del canal borraría el de cada número sin decirlo.
      const numeroDeLaFila = String(next.phone_number_id || '').trim()
      await Promise.all(
        ragAgents.map(async (agent) => {
          const conns = await agentsService.listAgentConnections(agent.id)
          const matches = conns.filter((c) =>
            connectionBelongsToRow(c, normalizedSource, numeroDeLaFila),
          )
          await Promise.all(matches.map((c) => agentsService.deleteAgentConnection(agent.id, c.id)))
        }),
      )

      if (next.agent_id !== 'none') {
        await agentsService.createAgentConnection(next.agent_id, {
          // Se conserva el `source_key` que ya tenía la conexión, no el de la fila del
          // catálogo: para el avatar son distintos (`liveavatar` vs `Avatar`) y recrearla
          // con el del catálogo dejaría al portal sin reconocer su propio canal.
          source_key: next.source_key ?? sourceKey,
          // La asignación recrea la conexión, así que hay que arrastrar el estado on/off
          // actual: si no, reasignar el agente reactivaría un canal que estaba apagado.
          status: next.status ?? 'connected',
          is_default: next.is_default,
          // El número que reclama, si la fila es de un número. Es lo que mira el
          // enrutado al recibir un mensaje.
          config: numeroDeLaFila ? { phone_number_id: numeroDeLaFila } : {},
        })
      }
    },
    [ragAgents],
  )

  /**
   * Enciende o apaga un canal sin tocar a qué agente está asignado.
   *
   * Para el canal `liveavatar` esto es el interruptor del avatar del portal de compradores:
   * el portal consulta este estado y deja de mostrarlo cuando no es `connected`.
   */
  const toggleChannelEnabled = async (row: ChannelRow, nextEnabled: boolean) => {
    const key = normalizeSourceKey(row.source_key)
    // Solo el avatar tiene interruptor: es el único canal cuyo `status` alguien consume
    // (lo lee el portal). Para el resto, escribirlo no apagaría nada y dejaría la conexión
    // en un estado que ninguna parte del sistema respeta.
    if (key !== AVATAR_CANONICAL_KEY) return
    const current = connectionIndexBySource[key]
    if (!current || current.agent_id === 'none' || !current.connection_id) {
      toast.error('Asigna un agente a este canal antes de activarlo o desactivarlo.')
      return
    }

    setSavingSourceKey(key)
    try {
      await agentsService.patchAgentConnection(current.agent_id, current.connection_id, {
        status: nextEnabled ? 'connected' : 'disabled',
      })
      await loadData({ withGlobalLoading: false })
      toast.success(nextEnabled ? 'Canal activado' : 'Canal desactivado')
    } catch (error) {
      log.error('ChannelAgentsPage toggle failed', error, { component: 'ChannelAgentsPage' })
      toast.error(toErrorMessage(error))
    } finally {
      setSavingSourceKey(null)
    }
  }

  const saveSourceAssignment = async (row: ChannelRow) => {
    const key = rowKey(row.source_key, row.phone_number_id)
    const current = connectionIndexBySource[key] || { agent_id: 'none', is_default: false }
    const nextAgentId = draftsBySource[key] ?? 'none'
    const nextIsDefault = nextAgentId === 'none' ? false : current.is_default
    setSavingSourceKey(key)
    try {
      await applyConnectionState(row.source_key, {
        agent_id: nextAgentId,
        is_default: nextIsDefault,
        // Conservar el on/off: cambiar de agente no debe reactivar un canal apagado.
        status: current.status ?? 'connected',
        // Y conservar la clave real de la conexión (ver applyConnectionState).
        source_key: current.source_key,
        phone_number_id: row.phone_number_id,
      })
      await loadData({ withGlobalLoading: false })
      toast.success('Asignacion actualizada')
    } catch (error) {
      log.error('ChannelAgentsPage saveSourceAssignment failed', error, { sourceKey: row.source_key })
      toast.error(toErrorMessage(error))
    } finally {
      setSavingSourceKey(null)
    }
  }

  const deleteCustomChannel = async (channel: ConversationSource) => {
    setDeletingSourceId(channel.id)
    try {
      await conversationsApi.deleteConversationSource(channel.id)
      toast.success('Canal eliminado')
      await loadData({ withGlobalLoading: false })
    } catch (error) {
      log.error('ChannelAgentsPage deleteCustomChannel failed', error, { channelId: channel.id })
      toast.error(toErrorMessage(error))
    } finally {
      setDeletingSourceId(null)
    }
  }

  const createCustomChannel = async () => {
    const key = newChannelKey.trim()
    const name = newChannelName.trim()
    if (!key || !name) return
    if (createSourceAlreadyExists) {
      toast.error('El source ya existe. Debe ser unico.')
      return
    }

    setCreatingChannel(true)
    try {
      await conversationsApi.createConversationSource({
        key,
        display_name: name,
        filter_tier: 'available',
        supports_internal_rag_agent: true,
        persistence_for_external_invoke: true,
        is_enabled: true,
      })

      if (createAssignedAgentId !== 'none') {
        await applyConnectionState(key, {
          agent_id: createAssignedAgentId,
          // `createAsDefault` no existia: se usaba aqui y no se declaraba en
          // ninguna parte, asi que crear un canal con agente lanzaba un
          // ReferenceError. El dialogo de crear no ofrece casilla de «por
          // defecto» -solo clave, nombre y agente-, y un canal recien creado no
          // lo es hasta que alguien lo marque. Mismo criterio que :517, donde
          // asignar un agente conserva el `is_default` que hubiera, que para un
          // canal nuevo es false.
          is_default: false,
        })
      }

      setCreateOpen(false)
      setNewChannelKey('')
      setNewChannelName('')
      setCreateAssignedAgentId('none')
      toast.success('Canal creado')
      await loadData({ withGlobalLoading: false })
    } catch (error) {
      log.error('ChannelAgentsPage createCustomChannel failed', error, { key, name })
      toast.error(toErrorMessage(error))
    } finally {
      setCreatingChannel(false)
    }
  }

  const closeEditDialog = () => {
    setEditOpen(false)
    setEditingRow(null)
    setEditChannelName('')
  }

  const openEditChannel = (row: ChannelRow) => {
    setEditingRow(row)
    setEditChannelName(row.display_name)
    setEditOpen(true)
  }

  const updateChannel = async () => {
    if (!editingRow?.is_custom || !editingRow.channel_id) return
    const nextDisplayName = editChannelName.trim()
    if (!nextDisplayName) return

    setUpdatingChannel(true)
    try {
      await conversationsApi.patchConversationSource(editingRow.channel_id, {
        display_name: nextDisplayName,
      })
      toast.success('Canal actualizado')
      closeEditDialog()
      await loadData({ withGlobalLoading: false })
    } catch (error) {
      log.error('ChannelAgentsPage updateChannel failed', error, {
        channelId: editingRow.channel_id,
        sourceKey: editingRow.source_key,
      })
      toast.error(toErrorMessage(error))
    } finally {
      setUpdatingChannel(false)
    }
  }

  const askDeleteCustomChannel = (channel: ConversationSource) => {
    setChannelPendingDelete(channel)
    setDeleteDialogOpen(true)
  }

  const confirmDeleteCustomChannel = () => {
    if (!channelPendingDelete) return
    void deleteCustomChannel(channelPendingDelete)
    setDeleteDialogOpen(false)
    setChannelPendingDelete(null)
  }

  return (
    <div className="container mx-auto space-y-4 p-3 md:p-6">
      <section>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-semibold tracking-tight">Agentes por canal</h1>
          {/* <Button type="button" size="sm" variant="secondary" onClick={openCreateChannelDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Crear canal
          </Button> */}
        </div>
        {changedSources.length > 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">Hay cambios sin guardar, usa Guardar en cada fila.</p>
        ) : null}
      </section>

      <section className="space-y-2">
        <h2 className="px-1 text-lg font-semibold">Canales existentes</h2>
        <p className="px-1 text-sm text-muted-foreground">
          Recupera el flujo previo: selecciona el agente por canal y guarda el cambio en cada fila.
        </p>
        {loading ? (
          <p className="px-1 text-sm text-muted-foreground">Cargando canales...</p>
        ) : channelRows.length === 0 ? (
          <p className="px-1 text-sm text-muted-foreground">No hay canales disponibles en el catalogo.</p>
        ) : (
          channelRows.map((row) => {
            const key = rowKey(row.source_key, row.phone_number_id)
            const current = connectionIndexBySource[key] || { agent_id: 'none', is_default: false }
            const selectedAgentId = draftsBySource[key] ?? current.agent_id
            const dirty = selectedAgentId !== current.agent_id
            const selectedAgent = ragAgents.find((agent) => normalizeId(agent.id) === selectedAgentId) || null
            const customChannel = customChannels.find((channel) => normalizeSourceKey(channel.key) === key) || null
            // El avatar es un caso aparte: no se le asigna agente desde aquí (responde el
            // agente de voz de ElevenLabs), solo se enciende o se apaga.
            const isAvatarRow = key === AVATAR_CANONICAL_KEY

            return (
              <article
                key={row.source_key}
                className={cn(
                  'rounded-lg border bg-card/80 p-4 transition-colors',
                  dirty && 'border-warning/50',
                )}
              >
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {row.phone_number_id && <span className="w-4" aria-hidden />}
                      <SourceIcon sourceKey={row.source_key} />
                      <p className="text-lg font-semibold">{row.display_name}</p>
                      {/*
                        El interruptor es SOLO para el avatar. En el resto de canales
                        escribía `status` de verdad en la conexión, pero nada lo consume:
                        el resultado era un botón que parecía apagar un canal y en realidad
                        solo dejaba la conexión en un estado incoherente.
                      */}
                      {isAvatarRow && current.agent_id !== 'none' && current.connection_id ? (
                        <span className="ml-1 flex items-center gap-2">
                          <Switch
                            checked={current.status !== 'disabled'}
                            disabled={savingSourceKey === key}
                            onCheckedChange={(next) => {
                              void toggleChannelEnabled(row, next)
                            }}
                            aria-label={`Activar o desactivar el canal ${row.display_name}`}
                          />
                          <span
                            className={cn(
                              'text-xs font-medium',
                              current.status === 'disabled' ? 'text-muted-foreground' : 'text-success',
                            )}
                          >
                            {current.status === 'disabled' ? 'Desactivado' : 'Activo'}
                          </span>
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {row.phone_number_id ? (
                        <>
                          número: <span className="font-mono">{row.phone_label}</span>
                        </>
                      ) : (
                        <>
                          source: <span className="font-mono">{row.source_key}</span>
                        </>
                      )}
                    </p>
                    {isAvatarRow ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Avatar de comprar.rkiglesias.com · el cambio tarda unos minutos en
                        aplicarse en la web
                      </p>
                    ) : row.phone_number_id ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Contesta solo por este número. Sin agente propio, contesta el del
                        canal.
                      </p>
                    ) : (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {whatsappNumbers.length >= 2 &&
                        normalizeSourceKey(row.source_key) === 'whatsapp'
                          ? 'Contesta por los números que no tengan agente propio'
                          : current.agent_id !== 'none' && current.is_default
                            ? 'Default para este canal'
                            : 'No default para este canal'}
                      </p>
                    )}
                  </div>
                  {isAvatarRow ? (
                    // El avatar no elige agente aquí: quien responde es el agente de voz de
                    // ElevenLabs (y hay uno por idioma, es/en), configurado en Call agents.
                    // Esta fila solo gobierna si el avatar se muestra en el portal web.
                    <p className="text-sm text-muted-foreground lg:max-w-[420px]">
                      El asistente de voz se configura en <strong>Call agents</strong>. Aquí solo
                      se decide si el avatar aparece en el portal web.
                    </p>
                  ) : (
                  <div className="flex w-full flex-col gap-2 lg:w-full lg:max-w-[880px] lg:flex-row lg:items-center">
                    <Popover
                      open={openSourceSelector === key}
                      onOpenChange={(nextOpen) => {
                        setOpenSourceSelector(nextOpen ? key : null)
                        if (!nextOpen) setSourceSearchTerm('')
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button size="lg"
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between overflow-hidden lg:flex-1"
                        >
                          <div className="min-w-0 flex-1 overflow-hidden">
                            {selectedAgent ? (
                              <AgentSummary agent={selectedAgent} visual={agentVisuals[normalizeId(selectedAgent.id)]} />
                            ) : (
                              <p className="truncate text-sm text-muted-foreground">Sin asignacion</p>
                            )}
                          </div>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[calc(100vw-2rem)] max-w-[440px] p-2" align="start">
                        <div className="relative mb-2">
                          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            placeholder="Buscar agente..."
                            value={sourceSearchTerm}
                            onChange={(event) => setSourceSearchTerm(event.target.value)}
                            className="h-9 pl-8"
                          />
                        </div>
                        <div className="max-h-[280px] space-y-1 overflow-auto pr-1">
                          <Button
                            variant="ghost"
                            size="xs"
                            className="w-full min-w-0 justify-start gap-2 text-left"
                            onClick={() => {
                              setDraftsBySource((prev) => ({ ...prev, [key]: 'none' }))
                              setOpenSourceSelector(null)
                              setSourceSearchTerm('')
                            }}
                          >
                            <Check className={cn('h-4 w-4', selectedAgentId === 'none' ? 'opacity-100' : 'opacity-0')} />
                            <div className="min-w-0">
                              <p className="text-sm font-medium">Sin asignacion</p>
                            </div>
                          </Button>

                          {filteredAgents.length === 0 ? (
                            <div className="py-3 text-center text-sm text-muted-foreground">
                              No se encontraron agentes.
                            </div>
                          ) : (
                            filteredAgents.map((agent) => (
                              <Button
                                key={normalizeId(agent.id)}
                                variant="ghost"
                                size="xs"
                                className="w-full min-w-0 justify-start gap-2 text-left"
                                onClick={() => {
                                  setDraftsBySource((prev) => ({
                                    ...prev,
                                    [key]: normalizeId(agent.id),
                                  }))
                                  setOpenSourceSelector(null)
                                  setSourceSearchTerm('')
                                }}
                              >
                                <Check
                                  className={cn(
                                    'h-4 w-4',
                                    selectedAgentId === normalizeId(agent.id) ? 'opacity-100' : 'opacity-0',
                                  )}
                                />
                                <AgentSummary agent={agent} visual={agentVisuals[normalizeId(agent.id)]} />
                              </Button>
                            ))
                          )}
                        </div>
                        <div className="mt-2 border-t border-border pt-2">
                          <Button
                            variant="ghost"
                            size="xs"
                            className="w-full justify-start gap-2 font-medium text-primary"
                            onClick={() => {
                              setOpenSourceSelector(null)
                              setSourceSearchTerm('')
                              navigate(`${langPrefix}/agent-studio`)
                            }}
                          >
                            <Plus className="h-4 w-4" />
                            Crear nuevo agente
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>

                    <Button
                      size="default"
                      onClick={() => void saveSourceAssignment(row)}
                      disabled={savingSourceKey !== null || !dirty}
                     
                    >
                      <Save className="mr-2 h-4 w-4" />
                      {savingSourceKey === key ? 'Guardando...' : 'Guardar'}
                    </Button>

                    {row.is_custom ? (
                      <Button size="default"
                        type="button"
                        variant="outline"
                       
                        disabled={deletingSourceId !== null || savingSourceKey !== null}
                        onClick={() => openEditChannel(row)}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        Editar canal
                      </Button>
                    ) : null}

                    {customChannel ? (
                      <Button size="default"
                        type="button"
                        variant="outline"
                        className="border-destructive/30 text-destructive hover:bg-destructive/10"
                        disabled={deletingSourceId !== null || savingSourceKey !== null}
                        onClick={() => askDeleteCustomChannel(customChannel)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Eliminar
                      </Button>
                    ) : null}
                  </div>
                  )}
                </div>
              </article>
            )
          })
        )}
      </section>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Crear canal</DialogTitle>
            <DialogDescription className="sr-only">
              Define el source y el nombre visible para crear un canal personalizado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <div className="space-y-2">
              <Label htmlFor="new-ch-key">Source</Label>
              <Input
                id="new-ch-key"
                placeholder="MiCanal"
                value={newChannelKey}
                onChange={(e) => setNewChannelKey(e.target.value)}
              />
              {createSourceAlreadyExists ? (
                <p className="text-xs text-destructive">Este source ya existe. Usa uno diferente.</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-ch-name">Nombre</Label>
              <Input
                id="new-ch-name"
                placeholder="Nombre visible"
                value={newChannelName}
                onChange={(e) => setNewChannelName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Asignacion</p>
              <div className="space-y-2">
                <Label htmlFor="create-assigned-agent">Agente</Label>
                <Select value={createAssignedAgentId} onValueChange={setCreateAssignedAgentId}>
                  <SelectTrigger id="create-assigned-agent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                  <SelectItem value="none">Sin agente</SelectItem>
                  {ragAgents.map((agent) => (
                    <SelectItem key={normalizeId(agent.id)} value={normalizeId(agent.id)}>
                      {agent.display_name}
                    </SelectItem>
                  ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => void createCustomChannel()}
              disabled={creatingChannel || !newChannelKey.trim() || !newChannelName.trim() || createSourceAlreadyExists}
            >
              {creatingChannel ? 'Creando...' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editOpen}
        onOpenChange={(open) => {
          if (!open) closeEditDialog()
          else setEditOpen(true)
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar canal</DialogTitle>
            <DialogDescription className="sr-only">
              Edita el source y el nombre visible de este canal personalizado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-ch-source">Source</Label>
              <Input id="edit-ch-source" value={editingRow?.source_key || ''} disabled readOnly />
              <p className="text-xs text-muted-foreground">El source es fijo para evitar inconsistencias.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-ch-name">Nombre</Label>
              <Input
                id="edit-ch-name"
                placeholder="Nombre visible"
                value={editChannelName}
                onChange={(e) => setEditChannelName(e.target.value)}
                disabled={!editingRow?.is_custom}
              />
              {!editingRow?.is_custom ? (
                <p className="text-xs text-muted-foreground">El nombre del canal base no se puede editar aqui.</p>
              ) : null}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeEditDialog} disabled={updatingChannel}>
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => void updateChannel()}
              disabled={updatingChannel || (editingRow?.is_custom ? !editChannelName.trim() : false)}
            >
              {updatingChannel ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open)
          if (!open) {
            setChannelPendingDelete(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar canal</AlertDialogTitle>
            <AlertDialogDescription>
              {channelPendingDelete
                ? `¿Seguro que quieres eliminar el canal "${channelPendingDelete.display_name}"?`
                : '¿Seguro que quieres eliminar este canal?'}
              <br />
              Esta accion no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingSourceId !== null}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteCustomChannel}
              disabled={deletingSourceId !== null || !channelPendingDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingSourceId !== null ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
